// #region Imports
import { useState, useEffect, ReactElement } from 'react'

import FileImport from '../FileImport/FileImport'
import styles from './ExcelFileImport.module.scss'

import { AWCButton, DropdownList } from '../Inputs/Inputs'
import { MenuItem } from '@material-ui/core'
import { SelectChangeEvent } from '@mui/material/Select'

import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'

import * as XLSX from 'xlsx'
import React from 'react'
import { LOADING_STATES } from '../../features/meta/metaSlice'
// #endregion

// #region Interfaces
export interface XLSXRow {
    name: string
    id: number
}

export enum EXCEL_DATA_TYPES {
    String,
    Decimal,
    Integer,
    Boolean,
    Date,
}

const getExcelColumn = (i: number): string => {
    let quotient = Math.floor(i / 26) //' Truncate
    if (quotient > 0) return `${getExcelColumn(quotient - 1)}${String.fromCharCode((i % 26) + 65)}`
    else return String.fromCharCode(i + 65)
}

interface ExcelFileImportProps {
    onFileDropped?(importJsonData: any[]): void
    onFileImported?(importData: any[]): void
    returnHeaderRowInResults?: boolean
    columnList?: Array<ExcelColumnMap>
}

interface ExcelColumnMap {
    fieldName: string
    fieldLabel: string
    dataType?: EXCEL_DATA_TYPES
    maxDataLength?: number
    required?: boolean
}

interface FieldDropdownListData {
    fieldName: string
    dropDownListMenuItemData: MappingListMenuItem[]
}

interface MappingListMenuItem {
    text: string
    value: number
}

interface FieldSelectedValueData {
    fieldLabel: string
    fieldName: string
    required?: boolean
    selectedIndex: number
}
// #endregion

export default function ExcelFileImport({
    returnHeaderRowInResults,
    onFileImported,
    onFileDropped,
    columnList,
}: ExcelFileImportProps) {
    const [excelData, setExcelData] = useState<any[]>([]) // the data imported from the Excel spreadsheet
    const [ddlData, setDDLData] = useState<FieldDropdownListData[]>([]) // mapping drop down list items
    const [selectedDDLData, setSelectedDDLData] = useState<FieldSelectedValueData[]>([]) // which drop down list items are selected

    useEffect(() => {
        if (!columnList) return

        // Initialize state DDL data
        const ddlDataInit: FieldDropdownListData[] = []
        columnList.forEach((f) =>
            ddlDataInit.push({
                fieldName: f.fieldName,
                dropDownListMenuItemData: [],
            })
        )
        setDDLData(ddlDataInit)

        // Initialize state selected value data
        const selectedDataInit: FieldSelectedValueData[] = []
        columnList.forEach((col) =>
            selectedDataInit.push({
                fieldLabel: col.fieldLabel,
                fieldName: col.fieldName,
                required: col.required,
                selectedIndex: -1,
            })
        )
        setSelectedDDLData(selectedDataInit)
    }, [])

    // #region MappingDropDownList
    interface MappingDropDownListProps {
        fieldName: string
    }

    const addValueToSelectedDDLData = (fieldName: string, selectedIndex: number) => {
        const selectedData = [...selectedDDLData]
        const selectedDataRow = selectedData.find((d) => d.fieldName === fieldName)
        if (selectedDataRow) {
            selectedDataRow.selectedIndex = selectedIndex
            setSelectedDDLData(selectedData)
        }
    }

    const getColumnHeaders = (data: any[]): string[] => {
        // Get first row to define headers
        const firstRow = data[0]
        const secondRow = data[1]
        // pad header row array with spaces
        const headerRow = firstRow
            .concat(Array(secondRow.length).fill(''))
            .slice(0, secondRow.length)
        return returnHeaderRowInResults ? Object.values(headerRow) : Object.keys(headerRow)
    }

    const MappingDropDownList = ({ fieldName }: MappingDropDownListProps) => {
        const data = ddlData.find((d) => d.fieldName === fieldName)?.dropDownListMenuItemData

        const value = selectedDDLData.find((d) => d.fieldName === fieldName)?.selectedIndex

        const onChange = (e: SelectChangeEvent) =>
            addValueToSelectedDDLData(fieldName, e.target.value as unknown as number)

        const items = data?.map((keyValue, i) => {
            return (
                <MenuItem key={`mappingDropDownList${i}`} value={keyValue.value}>
                    {keyValue.text}
                </MenuItem>
            )
        })

        return (
            <DropdownList
                onChange={onChange}
                value={value}
                style={{ backgroundColor: 'white', width: '275px' }}
            >
                {items}
            </DropdownList>
        )
    }
    // #endregion

    useEffect(() => {
        if (!excelData || excelData.length === 0) return

        let columnHeaders = getColumnHeaders(excelData)

        // Empty DDL data list to fill up and commit to state
        const newAllDDLData: FieldDropdownListData[] = []

        // Local copy of state data (initalized data on first load)
        const curDDLData = ddlData

        // Fill up empty array with column data from imported BOM file
        curDDLData.forEach((ddl) => {
            let newDDLMenuItemData = [{ text: 'Select Matching Column', value: -1 }]
            columnHeaders.forEach((headerText: string, i: number) => {
                // Excel Column (e.g. A, B, etc) has been mapped to a different field already -> don't add to list
                if (excelColumnUsed(i, ddl.fieldName)) return

                const excelCol = getExcelColumn(i)
                newDDLMenuItemData.push({
                    text: `[Col ${excelCol}] ${headerText}`,
                    value: i,
                })
            })
            newAllDDLData.push({
                fieldName: ddl.fieldName,
                dropDownListMenuItemData: newDDLMenuItemData,
            })
        })

        setDDLData(newAllDDLData)
    }, [excelData, selectedDDLData])

    const excelColumnUsed = (excelColumnIndex: number, currentFieldName: string = ''): boolean => {
        // returns true if a field is already mapped to an excel column (excelColumn)
        // unless that excel column is already mapped to the current field being mapped (currentFieldName)
        return selectedDDLData.some(
            (d) =>
                d.selectedIndex === excelColumnIndex &&
                (currentFieldName === '' || d.fieldName !== currentFieldName)
        )
    }

    interface ColumnMappingProps {
        columnList: Array<ExcelColumnMap>
    }

    const ColumnMapping = ({ columnList }: ColumnMappingProps) => {
        const requiredFieldsAreMapped = (): boolean => {
            const firstErrorField = selectedDDLData.find(
                (s) => s.required && (s.selectedIndex === undefined || s.selectedIndex === -1)
            )
            if (firstErrorField) {
                fqAlert({
                    type: 'error',
                    title: 'Required Field Missing',
                    message: `Please select a field for ${firstErrorField.fieldLabel}.`,
                })
                return false
            }

            return true
        }

        const onReviewClick = () => {
            if (!requiredFieldsAreMapped()) return

            let importData: any[] = []
            let headerPresent = true
            excelData?.forEach((xlRow, i) => {
                if (headerPresent && i === 0) return

                const checkExcelRow = xlRow.filter((n: any) => n)
                if (checkExcelRow.length === 0) return

                let importItem: { [k: string]: any } = { Row: { Value: i + 1 }, ErrorCount: 0 }

                columnList?.forEach((col, i) => {
                    const itemColumnIndex = selectedDDLData.find(
                        (d) => d.fieldName === col.fieldName
                    )!.selectedIndex

                    if (itemColumnIndex > -1) {
                        importItem[col.fieldName] = {}

                        let val = (xlRow as any[])[itemColumnIndex]
                        if (!val && val !== 0) val = ''
                        val = val.toString()

                        importItem[col.fieldName]['Value'] = val

                        const isNumeric = (value: string) => {
                            return /^-?\d+(\.\d+)?$/.test(value)
                        }

                        switch (col.dataType) {
                            case EXCEL_DATA_TYPES.Boolean:
                                if (
                                    val !== '' &&
                                    val.toLowerCase() !== 'true' &&
                                    val.toLowerCase() !== 'false'
                                )
                                    importItem[col.fieldName].Error = 'Value must be true or false!'
                                break
                            case EXCEL_DATA_TYPES.Date:
                                const d = Date.parse(val)
                                if (isNaN(d)) importItem[col.fieldName].Error = 'Invalid date!'
                                break
                            case EXCEL_DATA_TYPES.Decimal:
                                if (!isNumeric(val))
                                    importItem[col.fieldName].Error = 'Invalid decimal value!'
                                break
                            case EXCEL_DATA_TYPES.Integer:
                                if (isNaN(val) || parseInt(val) != val || isNaN(parseInt(val, 10)))
                                    importItem[col.fieldName].Error = 'Invalid integer value!'
                                break
                            case EXCEL_DATA_TYPES.String:
                                if (
                                    col.maxDataLength &&
                                    col.maxDataLength > 0 &&
                                    val.toString().length > col.maxDataLength
                                )
                                    importItem[col.fieldName][
                                        'Error'
                                    ] = `Max length (${col.maxDataLength}) exceeded!`
                                break
                            default:
                                return null
                        }
                        if (
                            importItem[col.fieldName].Error &&
                            importItem[col.fieldName].Error !== ''
                        )
                            importItem.ErrorCount = importItem.ErrorCount + 1
                    }
                })

                importData.push(importItem)
            })

            onFileImported && onFileImported(importData)
        }

        return (
            <>
                <div className={styles.importMapping}>
                    <div className={styles.importMappingLists}>
                        {columnList?.map((col, i) => {
                            const { required, fieldLabel, fieldName } = col
                            return (
                                <React.Fragment key={`map${i}`}>
                                    <div
                                        className={`${styles.mappingListItem} ${
                                            required ? styles.requiredAfter : ''
                                        }`}
                                        key={`mappingListItem${i}`}
                                    >
                                        <div className={styles.label}>{fieldLabel}</div>
                                        <div>
                                            <MappingDropDownList fieldName={fieldName} />
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                        })}
                    </div>
                    <div className={styles.mappingButton}>
                        <AWCButton type={'primary'} onClick={onReviewClick}>
                            Review
                        </AWCButton>
                    </div>
                </div>
                <div className={'a-divider'} />
            </>
        )
    }

    const onFileDrop = (b64FileData: string) => {
        const wb = XLSX.read(b64FileData, { cellDates: true })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const jsonData: any[] = XLSX.utils.sheet_to_json<any>(ws, {
            header: returnHeaderRowInResults ? 1 : 0,
            // blankrows: false,
        })

        // auto-select any columns with a header that matches the drop down label
        const columnHeaders = getColumnHeaders(jsonData)
        columnList?.forEach((c) => {
            const colIndex = columnHeaders.findIndex(
                (d: any) =>
                    d
                        .toString()
                        .trim()
                        .localeCompare(c.fieldLabel.trim(), 'en-US', { sensitivity: 'base' }) === 0 //case-insensitive compare
            )

            colIndex > -1 && addValueToSelectedDDLData(c.fieldName, colIndex)
        })

        setExcelData(jsonData)
        // if column list is defined, don't send imported data to parent component
        // will be sent later - once columns are mapped
        columnList
            ? onFileDropped && onFileDropped(jsonData)
            : onFileImported && onFileImported(jsonData)
    }

    return (
        <>
            <FileImport
                onDrop={(b64FileData) => {
                    onFileDrop(b64FileData)
                }}
                fileTypes={['xlsx']}
                fileSizeLimit={10000000}
            />
            {columnList && columnList.length > 0 && excelData?.length > 0 && (
                <ColumnMapping columnList={columnList} />
            )}
        </>
    )
}
